/**
 * Collision Detection Module - OBB-based collision system
 * 
 * Provides collision detection and resolution for:
 * - Standing on top of objects (platforms, floors, ramps)
 * - Side collisions (walls, obstacles)
 * 
 * Uses Three.js OBB (Oriented Bounding Box) for accurate rotation-aware collision detection.
 */

import { Vector3, Matrix4, Quaternion, Mesh, Object3D, BoxGeometry, MeshBasicMaterial } from 'three';
import { OBB } from 'three/examples/jsm/math/OBB.js';

/**
 * Collision result data
 */
export interface CollisionResult {
    /** Whether a collision occurred */
    hasCollision: boolean;
    /** Type of collision detected */
    type: 'ground' | 'wall' | 'ceiling' | 'none';
    /** Collision normal vector (direction to push out) */
    normal: Vector3;
    /** Penetration depth */
    depth: number;
    /** Correction vector to apply to resolve collision */
    correction: Vector3;
    /** The object that was collided with */
    collidedObject?: Object3D;
    /** Contact point in world space */
    contactPoint?: Vector3;
}

/**
 * Collision check options
 */
export interface CollisionCheckOptions {
    /** Tolerance for ground detection (how steep can a surface be to stand on) */
    groundAngleTolerance?: number;
    /** Minimum Y normal for ground detection (default: 0.7 for ~45° slopes) */
    minGroundNormal?: number;
    /** Enable ground collision detection */
    checkGround?: boolean;
    /** Enable wall collision detection */
    checkWalls?: boolean;
    /** Enable ceiling collision detection */
    checkCeiling?: boolean;
    /** Skin width - small buffer to prevent tunneling */
    skinWidth?: number;
}

/**
 * Collider configuration
 */
export interface ColliderConfig {
    /** Half extents of the collider box (width/2, height/2, depth/2) */
    halfExtents?: Vector3;
    /** Offset from object center */
    offset?: Vector3;
    /** Whether to visualize the collider (debug) */
    visualize?: boolean;
}

/**
 * OBB Collision Detector
 * 
 * Handles collision detection and resolution using Oriented Bounding Boxes.
 * Supports both ground detection (standing on platforms) and wall detection (side collisions).
 * 
 * @example
 * ```typescript
 * // Create collision detector for player
 * const collisionDetector = new CollisionDetector(playerMesh, {
 *   halfExtents: new Vector3(0.5, 1, 0.5) // Player size
 * });
 * 
 * // Check collision with platforms
 * const result = collisionDetector.checkCollision(platformMesh, {
 *   checkGround: true,
 *   checkWalls: true
 * });
 * 
 * if (result.hasCollision) {
 *   // Apply correction to player position
 *   playerMesh.position.add(result.correction);
 *   
 *   if (result.type === 'ground') {
 *     console.log('Standing on platform!');
 *   }
 * }
 * ```
 */
export class CollisionDetector {
    private target: Object3D;
    private obb: OBB;
    private config: Required<ColliderConfig>;
    private debugHelper?: Mesh;

    constructor(target: Object3D, config: ColliderConfig = {}) {
        this.target = target;
        this.config = {
            halfExtents: config.halfExtents || new Vector3(0.5, 1, 0.5),
            offset: config.offset || new Vector3(0, 0, 0),
            visualize: config.visualize || false,
        };

        // Create OBB for the target
        this.obb = new OBB();
        this.updateOBB();

        if (this.config.visualize) {
            this.createDebugHelper();
        }
    }

    /**
     * Update the OBB based on current target position and rotation
     */
    private updateOBB(): void {
        const position = this.target.position.clone().add(this.config.offset);
        this.obb.center.copy(position);
        this.obb.halfSize.copy(this.config.halfExtents);

        // Update rotation - OBB.rotation is a Matrix3
        const worldQuaternion = new Quaternion();
        this.target.getWorldQuaternion(worldQuaternion);
        const rotation = new Matrix4().makeRotationFromQuaternion(worldQuaternion);
        this.obb.rotation.setFromMatrix4(rotation);
    }

    /**
     * Create a visual debug helper for the collider
     */
    private createDebugHelper(): void {
        const geometry = new BoxGeometry(
            this.config.halfExtents.x * 2,
            this.config.halfExtents.y * 2,
            this.config.halfExtents.z * 2
        );
        const material = new MeshBasicMaterial({
            color: 0x00ff00,
            wireframe: true,
            transparent: true,
            opacity: 0.5,
        });
        this.debugHelper = new Mesh(geometry, material);
        this.debugHelper.position.copy(this.config.offset);
        this.target.add(this.debugHelper);
    }

    /**
     * Create OBB from a Three.js Object3D
     * 
     * Supports custom collision box sizes via object.userData:
     * - userData.collisionBox: { width, height, depth } - Custom box size
     * - userData.collisionScale: number - Scale factor for auto-calculated box (default: 1.0)
     */
    private createOBBFromObject(object: Object3D): OBB {
        const obb = new OBB();

        // Get world position
        const worldPosition = new Vector3();
        object.getWorldPosition(worldPosition);
        obb.center.copy(worldPosition);

        // Get world rotation - OBB.rotation is a Matrix3
        const worldQuaternion = new Quaternion();
        object.getWorldQuaternion(worldQuaternion);
        const rotation = new Matrix4().makeRotationFromQuaternion(worldQuaternion);
        obb.rotation.setFromMatrix4(rotation);

        // Check for custom collision box in userData
        if (object.userData && object.userData.collisionBox) {
            const customBox = object.userData.collisionBox;
            obb.halfSize.set(
                customBox.width / 2 || 0.5,
                customBox.height / 2 || 0.5,
                customBox.depth / 2 || 0.5
            );
            return obb;
        }

        // Calculate half size from bounding box
        let mesh: Mesh | undefined = undefined;

        // Check if object is a mesh
        if (object instanceof Mesh) {
            mesh = object;
        } else {
            // Try to find first mesh child
            object.traverse((child) => {
                if (child instanceof Mesh && !mesh) {
                    mesh = child;
                }
            });
        }

        if (mesh && mesh.geometry) {
            // Compute bounding box if not already computed
            if (!mesh.geometry.boundingBox) {
                mesh.geometry.computeBoundingBox();
            }

            const bbox = mesh.geometry.boundingBox;
            if (bbox) {
                const size = new Vector3();
                bbox.getSize(size);

                // Apply object scale
                const worldScale = new Vector3();
                object.getWorldScale(worldScale);
                size.multiply(worldScale);

                // Apply custom collision scale if specified in userData
                let collisionScale = 1.0;
                if (object.userData && object.userData.collisionScale) {
                    collisionScale = object.userData.collisionScale;
                } else if (object.name && (object.name.includes('obstacle') || object.name.includes('cylinder'))) {
                    // For cylindrical objects, shrink the box to better fit the circular shape
                    // This prevents collision boxes from being much larger than the visible cylinder
                    // Shrink X and Z by ~70% to approximate the inscribed box of a cylinder
                    size.x *= 0.7;
                    size.z *= 0.7;
                }

                // Apply collision scale
                if (collisionScale !== 1.0) {
                    size.multiplyScalar(collisionScale);
                }

                // Handle planes and thin geometry (minimum thickness of 0.1)
                const minThickness = 0.1;
                if (size.x < minThickness) size.x = minThickness;
                if (size.y < minThickness) size.y = minThickness;
                if (size.z < minThickness) size.z = minThickness;

                obb.halfSize.copy(size.multiplyScalar(0.5));
            } else {
                // Fallback: use default size
                obb.halfSize.set(0.5, 0.5, 0.5);
            }
        } else {
            // Fallback: use default size
            obb.halfSize.set(0.5, 0.5, 0.5);
        }

        return obb;
    }

    /**
     * Check collision between two OBBs
     * Returns separation axis and depth if collision detected
     */
    private checkOBBCollision(
        obb1: OBB,
        obb2: OBB
    ): { colliding: boolean; normal: Vector3; depth: number } {
        // Use Three.js OBB intersection test
        const intersects = obb1.intersectsOBB(obb2);

        if (!intersects) {
            return {
                colliding: false,
                normal: new Vector3(0, 1, 0),
                depth: 0,
            };
        }

        // Calculate separation vector (from obb2 to obb1)
        const separation = new Vector3().subVectors(obb1.center, obb2.center);
        const distance = separation.length();

        if (distance < 0.001) {
            // Objects are at same position, push upward
            return {
                colliding: true,
                normal: new Vector3(0, 1, 0),
                depth: 0.1,
            };
        }

        // Test all 15 axes (SAT - Separating Axis Theorem):
        // 3 axes from obb1, 3 axes from obb2, and 9 cross products
        const axes: Vector3[] = [];

        // Get OBB1 axes (rotation matrix columns)
        const obb1Axes = [
            new Vector3(obb1.rotation.elements[0], obb1.rotation.elements[1], obb1.rotation.elements[2]),
            new Vector3(obb1.rotation.elements[3], obb1.rotation.elements[4], obb1.rotation.elements[5]),
            new Vector3(obb1.rotation.elements[6], obb1.rotation.elements[7], obb1.rotation.elements[8])
        ];

        // Get OBB2 axes (rotation matrix columns)
        const obb2Axes = [
            new Vector3(obb2.rotation.elements[0], obb2.rotation.elements[1], obb2.rotation.elements[2]),
            new Vector3(obb2.rotation.elements[3], obb2.rotation.elements[4], obb2.rotation.elements[5]),
            new Vector3(obb2.rotation.elements[6], obb2.rotation.elements[7], obb2.rotation.elements[8])
        ];

        // Add face normals from both OBBs
        axes.push(...obb1Axes, ...obb2Axes);

        // Add cross products of edges (important for rotated boxes)
        for (const axis1 of obb1Axes) {
            for (const axis2 of obb2Axes) {
                const cross = new Vector3().crossVectors(axis1, axis2);
                if (cross.lengthSq() > 0.0001) { // Ignore nearly parallel axes
                    cross.normalize();
                    axes.push(cross);
                }
            }
        }

        let minDepth = Infinity;
        let bestNormal = new Vector3(0, 1, 0);

        // Test each axis
        for (const axis of axes) {
            // Project OBB1 onto axis
            let proj1 = 0;
            for (let i = 0; i < 3; i++) {
                proj1 += Math.abs(obb1.halfSize.getComponent(i) * obb1Axes[i].dot(axis));
            }

            // Project OBB2 onto axis
            let proj2 = 0;
            for (let i = 0; i < 3; i++) {
                proj2 += Math.abs(obb2.halfSize.getComponent(i) * obb2Axes[i].dot(axis));
            }

            // Project separation onto axis
            const projSep = Math.abs(separation.dot(axis));

            // Calculate penetration depth on this axis
            const penetration = (proj1 + proj2) - projSep;

            // Keep track of minimum penetration
            if (penetration > 0 && penetration < minDepth) {
                minDepth = penetration;
                // Normal should point from obb2 to obb1
                const sign = separation.dot(axis) >= 0 ? 1 : -1;
                bestNormal = axis.clone().multiplyScalar(sign);
            }
        }

        return {
            colliding: true,
            normal: bestNormal,
            depth: minDepth < Infinity ? minDepth : 0.1,
        };
    }

    /**
     * Determine collision type based on normal direction
     */
    private determineCollisionType(
        normal: Vector3,
        minGroundNormal: number
    ): 'ground' | 'wall' | 'ceiling' {
        // Ground: normal points mostly upward
        if (normal.y >= minGroundNormal) {
            return 'ground';
        }
        // Ceiling: normal points mostly downward
        else if (normal.y <= -minGroundNormal) {
            return 'ceiling';
        }
        // Wall: normal is mostly horizontal
        else {
            return 'wall';
        }
    }

    /**
     * Calculate collision correction vector
     */
    private calculateCorrection(
        normal: Vector3,
        depth: number,
        skinWidth: number
    ): Vector3 {
        // Cap depth to prevent extreme teleportation
        const cappedDepth = Math.min(depth, 2.0);
        const correction = normal.clone().multiplyScalar(cappedDepth + skinWidth);

        // Don't filter correction by type - rotated surfaces need all components
        // The normal already contains the correct direction to push the object out
        // Filtering can cause objects to fall through rotated planes

        return correction;
    }

    /**
     * Check collision with another object
     * 
     * @param other - Object to check collision against
     * @param options - Collision check options
     * @returns Collision result with correction vector
     */
    checkCollision(
        other: Object3D,
        options: CollisionCheckOptions = {}
    ): CollisionResult {
        const opts = {
            minGroundNormal: options.minGroundNormal ?? 0.7,
            checkGround: options.checkGround ?? true,
            checkWalls: options.checkWalls ?? true,
            checkCeiling: options.checkCeiling ?? false,
            skinWidth: options.skinWidth ?? 0.01,
        };

        // Update our OBB
        this.updateOBB();

        // Create OBB for other object
        const otherOBB = this.createOBBFromObject(other);

        // Check collision
        const collision = this.checkOBBCollision(this.obb, otherOBB);

        if (!collision.colliding) {
            return {
                hasCollision: false,
                type: 'none',
                normal: new Vector3(0, 1, 0),
                depth: 0,
                correction: new Vector3(0, 0, 0),
            };
        }

        // Determine collision type
        const type = this.determineCollisionType(
            collision.normal,
            opts.minGroundNormal
        );

        // Check if this type of collision should be processed
        if (
            (type === 'ground' && !opts.checkGround) ||
            (type === 'wall' && !opts.checkWalls) ||
            (type === 'ceiling' && !opts.checkCeiling)
        ) {
            return {
                hasCollision: false,
                type: 'none',
                normal: collision.normal,
                depth: collision.depth,
                correction: new Vector3(0, 0, 0),
            };
        }

        // Calculate correction vector
        const correction = this.calculateCorrection(
            collision.normal,
            collision.depth,
            opts.skinWidth
        );

        // Calculate contact point (approximate)
        const contactPoint = new Vector3()
            .addVectors(this.obb.center, otherOBB.center)
            .multiplyScalar(0.5);

        return {
            hasCollision: true,
            type,
            normal: collision.normal,
            depth: collision.depth,
            correction,
            collidedObject: other,
            contactPoint,
        };
    }

    /**
     * Check collisions with multiple objects
     * Returns array of collision results
     */
    checkCollisions(
        objects: Object3D[],
        options: CollisionCheckOptions = {}
    ): CollisionResult[] {
        const results: CollisionResult[] = [];

        for (const obj of objects) {
            const result = this.checkCollision(obj, options);
            if (result.hasCollision) {
                results.push(result);
            }
        }

        return results;
    }

    /**
     * Check if grounded (standing on something)
     * Convenience method for ground-only detection
     */
    isGrounded(objects: Object3D[], options: CollisionCheckOptions = {}): boolean {
        const results = this.checkCollisions(objects, {
            ...options,
            checkGround: true,
            checkWalls: false,
            checkCeiling: false,
        });

        return results.some((r) => r.type === 'ground');
    }

    /**
     * Get combined collision correction from multiple objects
     * Useful when colliding with multiple objects simultaneously
     */
    getCombinedCorrection(
        objects: Object3D[],
        options: CollisionCheckOptions = {}
    ): { correction: Vector3; hasCollisions: boolean; results: CollisionResult[] } {
        const results = this.checkCollisions(objects, options);

        if (results.length === 0) {
            return {
                correction: new Vector3(0, 0, 0),
                hasCollisions: false,
                results: [],
            };
        }

        // Combine corrections
        const combinedCorrection = new Vector3();
        for (const result of results) {
            combinedCorrection.add(result.correction);
        }

        return {
            correction: combinedCorrection,
            hasCollisions: true,
            results,
        };
    }

    /**
     * Update collider configuration
     */
    updateConfig(config: Partial<ColliderConfig>): void {
        if (config.halfExtents) {
            this.config.halfExtents.copy(config.halfExtents);
        }
        if (config.offset) {
            this.config.offset.copy(config.offset);
        }
        if (config.visualize !== undefined) {
            this.config.visualize = config.visualize;
            if (config.visualize && !this.debugHelper) {
                this.createDebugHelper();
            } else if (!config.visualize && this.debugHelper) {
                this.debugHelper.removeFromParent();
                this.debugHelper = undefined;
            }
        }
    }

    /**
     * Get the current OBB (for debugging or custom checks)
     */
    getOBB(): OBB {
        this.updateOBB();
        return this.obb;
    }

    /**
     * Cleanup resources
     */
    dispose(): void {
        if (this.debugHelper) {
            this.debugHelper.removeFromParent();
            this.debugHelper.geometry.dispose();
            (this.debugHelper.material as any).dispose();
            this.debugHelper = undefined;
        }
    }
}

/**
 * Utility: Create a simple collision detector for quick checks
 */
export function createCollisionDetector(
    target: Object3D,
    halfExtents?: Vector3
): CollisionDetector {
    return new CollisionDetector(target, { halfExtents });
}

/**
 * Utility: Quick collision check between two objects
 */
export function checkCollisionBetween(
    obj1: Object3D,
    obj2: Object3D,
    options: CollisionCheckOptions = {}
): CollisionResult {
    const detector = new CollisionDetector(obj1);
    const result = detector.checkCollision(obj2, options);
    detector.dispose();
    return result;
}

